// Message API 操作
import { AxiosInstance } from "axios";
import { InfoType, Message } from "../types";
import moment from "moment";
import { IS_LOCALHOST, IS_SERVER, TOKEN_COOKIE } from "constant/index";
import { getCookieByKey, Cookies } from "utils";
import { infoTagsToMentions, normalizeInfoTags, normalizeMessageInfoTags } from "../utils/tags";

/**
 * Message API 响应类型
 */
export interface MessageApiResponse {
  id: string;
  topicId: string;
  message: string | Message;
  messageJSON?: string | Message;
  messageJson?: string | Message;
  role: "user" | "assistant";
  createdDate: string;
  lastModifiedDate?: string;
}

const toMessageContent = (value: unknown) =>
  value === undefined || value === null ? "" : String(value);

const normalizePromptMeta = (value: unknown) => {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return undefined;
  }

  const promptMeta = value as Partial<NonNullable<Message["promptMeta"]>>;
  const title = toMessageContent(promptMeta.title).trim();

  if (!title) {
    return undefined;
  }

  return {
    id: toMessageContent(promptMeta.id),
    title,
    version: toMessageContent(promptMeta.version),
  };
};

const parseMessagePayload = (item: MessageApiResponse): Message => {
  const rawPayload = item.messageJSON ?? item.messageJson ?? item.message;
  let parsedPayload: unknown = rawPayload;

  if (typeof rawPayload === "string") {
    try {
      parsedPayload = JSON.parse(rawPayload);
    } catch {
      return {
        id: item.id,
        role: item.role,
        content: rawPayload,
        time: item.createdDate,
      };
    }
  }

  if (!parsedPayload || typeof parsedPayload !== "object" || Array.isArray(parsedPayload)) {
    return {
      id: item.id,
      role: item.role,
      content: toMessageContent(parsedPayload),
      time: item.createdDate,
    };
  }

  const message = parsedPayload as Partial<Message>;
  const content = toMessageContent(message.content);
  const promptMeta = normalizePromptMeta(message.promptMeta);
  const prompt =
    message.prompt ??
    (promptMeta
      ? {
          id: promptMeta.id,
          title: promptMeta.title,
          description: promptMeta.title,
          prompt: content,
          version: promptMeta.version,
        }
      : undefined);

  return normalizeMessageInfoTags({
    ...message,
    id: message.id ?? item.id,
    role: message.role ?? item.role,
    content: promptMeta?.title ?? content,
    ...(prompt ? { prompt } : {}),
    ...(promptMeta ? { promptMeta } : {}),
    time: message.time ?? item.createdDate,
  } as Message);
};

/**
 * 创建消息请求类型
 */

/**
 * 获取指定 Topic 的消息列表（按创建时间升序）
 * @param axios axios 实例
 * @param topicId Topic ID
 */
export async function fetchMessages(axios: AxiosInstance, topicId: string): Promise<Message[]> {
  try {
    const response = await axios.get<MessageApiResponse[]>(
      `/api/dataservice/v1/ai-chat/topics/${topicId}/messages`
    );

    const messages: Message[] = Array.isArray(response.data)
      ? response.data.map(parseMessagePayload)
      : [];

    return messages;
  } catch (error) {
    console.error(`Failed to fetch messages for topic ${topicId}:`, error);
    throw error;
  }
}

/**
 * 创建新消息
 * @param axios axios 实例
 * @param topicId Topic ID
 * @param data 消息数据
 */
export async function createMessage(
  axios: AxiosInstance,
  topicId: string,
  data: Message
): Promise<Message> {
  try {
    await axios.post<MessageApiResponse>(
      `/api/dataservice/v1/ai-chat/topics/${topicId}/messages`,
      data
    );

    return data;
  } catch (error) {
    console.error(`Failed to create message for topic ${topicId}:`, error);
    throw error;
  }
}

export const sendMessage = async (
  axios: AxiosInstance,
  toSendMessages: Message[],
  model: string,
  accountTags: InfoType[],
  setChatlist: (e: any) => void,
  chatId: string
): Promise<[string | null, Message | null]> => {
  if (!toSendMessages || toSendMessages.length === 0) return [null, null];

  const getFetchErrorMessage = async (res: Response) => {
    try {
      const data: any = await res.clone().json();
      return data?.message || data?.error || data?.detail || `Request failed (${res.status})`;
    } catch {
      try {
        const text = await res.clone().text();
        return text || `Request failed (${res.status})`;
      } catch {
        return `Request failed (${res.status})`;
      }
    }
  };

  const errorFn = (errorMsg: string) => {
    setChatlist((prev: any) =>
      prev.map((item: any) => {
        if (item.id === chatId) {
          return {
            ...item,
            content: [
              ...toSendMessages.slice(0, -1),
              {
                role: "assistant",
                content: `Error: ${errorMsg}`,
                accountTags,
              },
            ],
            accountTags,
            mentions: infoTagsToMentions(accountTags),
          };
        }
        return item;
      })
    );
  };

  const currentModel = model;

  try {
    const messages = toSendMessages
      .filter(
        (msg) =>
          msg.role === "user" ||
          msg.role === "assistant" ||
          (msg.role === "system" && msg.addedData)
      )
      .map((msg) => ({
        role: msg.role === "system" ? "user" : msg.role,
        content:
          msg.role === "system"
            ? (msg.addedData ?? "") + " " + msg.content
            : msg.role === "user" && msg.prompt
              ? msg.prompt.prompt
              : msg.content,
      }));

    let result;
    let lastError: any;
    for (let attempt = 1; attempt <= 3; attempt++) {
      try {
        const token = IS_SERVER ? getCookieByKey(TOKEN_COOKIE, "") : Cookies.get(TOKEN_COOKIE);
        result = await fetch(
          !IS_LOCALHOST
            ? "/csdigital/v1/accounts/ai-chat/stream"
            : "/api/csdigital/v1/accounts/ai-chat/stream",
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Accept: "text/event-stream",
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({
              messages,
              max_tokens: 4000,
              temperature: 0.7,
              model,
            }),
          }
        );
        if (!result.ok) {
          const errorMessage = await getFetchErrorMessage(result);
          throw new Error(errorMessage);
        }
        break; // Success, exit retry loop
      } catch (error) {
        lastError = error;
        console.error(`AI API call attempt ${attempt} failed:`, error);
        if (attempt < 3) {
          // Wait before retry (exponential backoff)
          await new Promise((resolve) => setTimeout(resolve, 2000 * attempt));
        } else {
          const errorMessage = lastError?.message ?? "Failed to send messages.";
          errorFn(errorMessage);
          return [errorMessage, null];
        }
      }
    }

    if (!result) {
      throw lastError || new Error("AI API call failed after 3 attempts");
    }

    if (result) {
      setChatlist((prev: any) =>
        prev.map((item: any) => {
          if (item.id === chatId) {
            return {
              ...item,
              content: [...toSendMessages.slice(0, -1)],
              accountTags,
              mentions: infoTagsToMentions(accountTags),
            };
          }
          return item;
        })
      );
      const reader: any = result.body?.getReader();
      const decoder = new TextDecoder("utf-8");
      let buffer = "";
      let output = "";
      while (true) {
        const { value, done } = await reader?.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n");
        buffer = lines.pop() || "";
        for (let line of lines) {
          if (line.startsWith("data:")) {
            line = line.replace(/^data:\s*/g, "");
            try {
              const json = JSON.parse(!!line ? line : "[]");
              const delta = json?.choices?.[0]?.delta;
              const con = delta?.content;
              if (con) {
                output += con;
                setChatlist((prev: any) =>
                  prev.map((item: any) => {
                    if (item.id === chatId) {
                      const existingContent = item.content || [];
                      const lastIndex = existingContent.length - 1;
                      const lastItem = existingContent[lastIndex];
                      const nextContent = [...existingContent];
                      if (lastItem?.role === "assistant") {
                        nextContent[lastIndex] = {
                          ...lastItem,
                          content: output,
                        };
                      } else {
                        nextContent.push({
                          id: Date.now().toString(),
                          role: "assistant",
                          content: output,
                          time: moment().format("MM/DD/YYYY h:mm:ss A"),
                          accountTags: normalizeInfoTags(
                            toSendMessages[toSendMessages.length - 1].accountTags ?? accountTags
                          ),
                          model: currentModel,
                        });
                      }
                      return {
                        ...item,
                        content: nextContent,
                        accountTags,
                        mentions: infoTagsToMentions(accountTags),
                      };
                    }
                    return item;
                  })
                );
              }
            } catch (e) {
              console.error("error:", e, line);
            }
          }
        }
      }
      return [
        null,
        {
          id: Date.now().toString(),
          role: "assistant",
          content: output,
          time: moment().format("MM/DD/YYYY h:mm:ss A"),
          accountTags: normalizeInfoTags(
            toSendMessages[toSendMessages.length - 1].accountTags ?? accountTags
          ),
          model: currentModel,
        },
      ];
    }
  } catch (error: any) {
    console.error("Failed to call AI API:", error);
    const errorMessage = error?.message ?? "Failed to send messages.";
    errorFn(errorMessage);
    return [errorMessage, null];
  }
  // Ensure a return value in all code paths
  errorFn("Failed to send messages.");
  return ["Failed to send messages.", null];
};

/**
 * Message Actions Hook
 * @deprecated 已弃用，请使用 AppStore 中的 useAppActions
 * 保留此函数仅为向后兼容
 */
export function useMessageActions(axios: AxiosInstance) {
  /**
   * 加载消息列表
   */
  const loadMessages = async (topicId: string): Promise<Message[]> => {
    try {
      const messages = await fetchMessages(axios, topicId);
      return messages;
    } catch (error: any) {
      console.error("Failed to load messages:", error);
      throw error;
    }
  };

  /**
   * 发送新消息
   */
  const addMessage = async (topicId: string, message: Message): Promise<Message> => {
    try {
      const newMessage = await createMessage(axios, topicId, message);
      return newMessage;
    } catch (error: any) {
      console.error("Failed to send message:", error);
      throw error;
    }
  };

  return {
    loadMessages,
    addMessage,
  };
}
